import { useState, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  getCroppedImgFromElement,
  rotateImage,
} from "../../utils/cropImage";

const EcgStripCropModal = ({ imageSrc, onSave, onCancel, saving }) => {
  const imgRef = useRef(null);
  const [step, setStep] = useState("crop"); // "rotate" | "crop"
  const [angle, setAngle] = useState(0);
  const [workingSrc, setWorkingSrc] = useState(imageSrc);
  const [baking, setBaking] = useState(false);

  const [crop, setCrop] = useState({ unit: "%", x: 10, y: 10, width: 80, height: 80 });
  const [completedCrop, setCompletedCrop] = useState(null);

  const handleContinueToCrop = async () => {
    if (angle === 0) {
      setStep("crop");
      return;
    }
    setBaking(true);
    try {
      const rotatedBlob = await rotateImage(workingSrc, angle);
      setWorkingSrc(URL.createObjectURL(rotatedBlob));
      setAngle(0);
      setStep("crop");
    } catch (err) {
      console.error("Error rotating ECG strip:", err);
      window.alert("Failed to rotate the image. Please try again.");
    }
    setBaking(false);
  };

  const handleOpenRotate = () => {
    setStep("rotate");
    setCompletedCrop(null);
  };

  // Quick full 90° turn — bakes immediately, separate from the fine
  // ±45° straighten slider below. Works the same for the main strip
  // and a duplicate, since this modal doesn't know or care which one
  // it's editing — the caller decides where to save on "Save Crop".
  const handleQuickRotate = async (delta) => {
    if (baking) return;
    setBaking(true);
    try {
      const rotatedBlob = await rotateImage(workingSrc, delta);
      setWorkingSrc(URL.createObjectURL(rotatedBlob));
    } catch (err) {
      console.error("Error rotating ECG strip:", err);
      window.alert("Failed to rotate the image. Please try again.");
    }
    setBaking(false);
  };

  // Fires once the image has actually loaded. Switch the crop box to
  // real pixel coordinates (matching the image as displayed on screen)
  // instead of percentages, since getCroppedImgFromElement expects that.
  const handleImageLoaded = (image) => {
    imgRef.current = image;
    const initialCrop = {
      unit: "px",
      x: image.width * 0.1,
      y: image.height * 0.1,
      width: image.width * 0.8,
      height: image.height * 0.8,
    };
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
    return false;
  };

  const handleSaveCrop = async () => {
    if (!completedCrop || !imgRef.current) return;
    const croppedBlob = await getCroppedImgFromElement(
      imgRef.current,
      completedCrop
    );
    onSave(croppedBlob);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.75)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          padding: "20px",
        }}
      >
        {step === "rotate" ? (
          <img
            src={workingSrc}
            alt="ECG strip to rotate"
            style={{
              maxHeight: "70vh",
              maxWidth: "80vw",
              transform: `rotate(${angle}deg)`,
            }}
          />
        ) : (
          <ReactCrop
            src={workingSrc}
            crop={crop}
            onChange={(newCrop) => setCrop(newCrop)}
            onComplete={(newCrop) => setCompletedCrop(newCrop)}
            onImageLoaded={handleImageLoaded}
          />
        )}
      </div>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {step === "rotate" ? (
          <>
            <button onClick={() => handleQuickRotate(-90)} disabled={baking || saving}>
              ↺ 90°
            </button>
            <button onClick={() => handleQuickRotate(90)} disabled={baking || saving}>
              ↻ 90°
            </button>
            <label style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
              Straighten
              <input
                type="range"
                min={-45}
                max={45}
                step={0.5}
                value={angle}
                onChange={(e) => setAngle(Number(e.target.value))}
                style={{ width: "220px" }}
              />
              <span style={{ minWidth: "40px" }}>{angle}°</span>
            </label>
            <button onClick={handleContinueToCrop} disabled={baking || saving}>
              {baking ? "Applying..." : "Back to Crop"}
            </button>
          </>
        ) : (
          <>
            <span style={{ fontSize: "13px", color: "#555" }}>
              Drag the box or its corners to select the area you want to keep.
            </span>
            <button onClick={handleOpenRotate} disabled={saving}>
              ⟲ Straighten
            </button>
            <button onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button
              onClick={handleSaveCrop}
              disabled={saving || !completedCrop}
            >
              {saving ? "Saving..." : "Save Crop"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EcgStripCropModal;
